import { Prettify } from '@fullstory/typeutils';
import { isArray } from 'lodash';
import { generatePath, matchPath, RouteComponentProps, useParams } from 'react-router-dom';

// Route utils
export type Params = Record<string, string | undefined>;
export type QueryParams = Record<
  string,
  string | number | boolean | string[] | number[] | boolean[]
>;

export type QueryParamValidator<P> = (payload: { [key: string]: string | string[] }) => P;
export interface Route<
  Path extends string = '',
  P extends Params = ExtractRouteParams<Path, string>,
  Q extends QueryParams | null = null,
  QV extends QueryParamValidator<Q> | undefined | null = null,
> {
  path: Path;
  // the saved validated query params on the given route - internal/private
  _validateQueryParams?: QV;
  chain: <C extends string, B extends Params = ExtractRouteParams<C, string>>(
    next: C,
  ) => Route<`${Path}${C}`, Prettify<P & B>, null, null>;
  queryParams: <
    QP extends QueryParams,
    QV extends QueryParamValidator<QP> | null | undefined = null,
  >(
    // an optional validation function. Must ensure that the given payload matches
    // the expected type of query params. This provides the ability to also drop
    // unexpected params.
    validate?: QV,
  ) => Route<Path, P, QP, QV>;
}

export const ROOT_ROUTE: Route<'', {}, null, null> = {
  path: '',
  _validateQueryParams: undefined,
  chain<C extends string>(next: C) {
    return {
      path: (this.path + next) as `${C}`,
      chain: this.chain,
      queryParams: this.queryParams as <
        QP extends QueryParams,
        QV extends QueryParamValidator<QP> | null | undefined = null,
      >(
        // an optional validation function. Must ensure that the given payload matches
        // the expected type of query params. This provides the ability to also drop
        // unexpected params.
        validate?: QV,
      ) => Route<`${C}`, {}, QP, QV>,
      _validateQueryParams: undefined,
    };
  },
  queryParams(validate) {
    return {
      path: this.path,
      chain: this.chain,
      queryParams: this.queryParams,
      _validateQueryParams: validate,
    };
  },
};

// Helper type to get the URL params from the given route
export type ParamsFrom<R> = R extends Route<infer Path, infer P, infer Q, infer QV> ? P : never;
// Helper type to get the URL Query params from the given route
export type QueryFrom<R> = R extends Route<infer Path, infer P, infer Q, infer QV> ? Q : never;
// Helper to get the react router RouteProps from the given route
export type RoutePropsFrom<T> = RouteComponentProps<ParamsFrom<T>>;

// Utility to convert a search string, like "?a=one&a=two&b=one" into a parallel object
// { a: ['one', 'two'], b: 'one' }
// Not intended for use outside of this package
export const paramsToRecord = (searchString: string) => {
  const urlParams = new URLSearchParams(searchString);
  const params: { [key: string]: string | string[] } = {};
  urlParams.forEach((value, key) => {
    const currentValue = params[key];
    if (typeof currentValue === 'string') {
      params[key] = [currentValue, value];
    } else if (isArray(currentValue)) {
      currentValue.push(value);
    } else {
      params[key] = value;
    }
  });
  return params;
};

export function generateRoutePath<
  R extends Route<string, Params, QueryParams | null, QueryParamValidator<QueryParams> | null>,
>(route: R, params: ParamsFrom<R>, query?: QueryFrom<R>, existingSearch?: string): string {
  if (query || existingSearch) {
    let searchParams: URLSearchParams;
    if (route._validateQueryParams) {
      const validatedParams = route._validateQueryParams(paramsToRecord(existingSearch ?? ''));
      searchParams = new URLSearchParams();
      Object.keys(validatedParams).forEach(k => {
        const value = validatedParams[k];
        if (isArray(value)) {
          value.forEach(v => searchParams.append(k, `${v}`));
        } else if (value != undefined) {
          searchParams.append(k, `${value}`);
        }
      });
    } else {
      searchParams = new URLSearchParams(existingSearch);
    }
    for (const key in query) {
      const value = query[key];
      if (value) {
        if (isArray(value)) {
          searchParams.delete(key);
          value.forEach(v => searchParams.append(key, `${v}`));
        } else {
          searchParams.set(key, `${value}`);
        }
      }
    }
    return `${generatePath(route.path, params)}?${searchParams.toString()}`;
  }
  return generatePath(route.path, params);
}

export const matchRoutePath = <
  R extends Route<string, Params, null | QueryParams, QueryParamValidator<QueryParams> | null>,
>(
  where: string,
  route: R,
) => matchPath<ParamsFrom<R>>(where, route.path);

/**
 * Asserts non-narrowed types for route params. If `useParams` is used directly it widens
 * all param types to string even if the type is narrowed to a union of string literals.
 */
export const useRouteParams = <
  R extends Route<string, Params, null | QueryParams, QueryParamValidator<QueryParams> | null>,
>() => useParams() as ParamsFrom<R>;

/**
 * There is an edge case when using a `Redirect` inside of a `Switch` where if the `to`
 * prop contains any colons (for example, as delimiters like the `segmentId` param
 * `'user@fullstory.com:6520960461635584'`) it leads to the `Redirect` component interpreting
 * them as a route param and throwing an error since they are missing from the computed
 * `match` object (e.g. 'expected ":6520960461635584" to be defined'). In those rare cases
 * we need to escape the colons so that `Redirect` does not treat them as params.
 * @param path Route path with colon delimiters.
 * @returns The input route path with the colon delimiters escaped.
 */
export const escapeColonDelimitedPath = (path: string) => path.replace(/:/g, '\\:');

/**
 * Checks if the given dest query parameter points to a URL that points to a specific org i.e. /ui/<orgId>.
 * OrgIds can have alphanumeric characters, dash, and period.
 * @param dest
 */
export const isDestWithOrgId = (dest: string = ''): boolean => /^\/ui\/[\w|-|.]+/.test(dest);

/**
 * Parses out the org ID section of a given dest query parameter i.e. /ui/<orgId>.
 * @param dest
 */
export const getOrgIdFromDest = (dest: string = ''): string => dest.split('/')[2] ?? '';

// Route Utilities pulled from a later version of @types/react-router@5: https://www.npmjs.com/package/@types/react-router/v/5.1.20
// TODO(vasilios): Remove ExtractRouteParams and ExtractRouteOptionalParam after we upgrade React to 18 (and react-router to its latest types)
type ExtractRouteOptionalParam<
  T extends string,
  U = string | number | boolean,
> = T extends `${infer Param}?`
  ? { [k in Param]?: U }
  : T extends `${infer Param}*`
  ? { [k in Param]?: U }
  : T extends `${infer Param}+`
  ? { [k in Param]: U }
  : { [k in T]: U };

export type ExtractRouteParams<T extends string, U = string | number | boolean> = string extends T
  ? { [k in string]?: U }
  : T extends `${infer _Start}:${infer ParamWithOptionalRegExp}/${infer Rest}`
  ? ParamWithOptionalRegExp extends `${infer Param}(${infer _RegExp})${infer Modifier extends
      | '?'
      | '+'
      | '*'
      | ''}`
    ? ExtractRouteOptionalParam<`${Param}${Modifier}`, U> & ExtractRouteParams<Rest, U>
    : ExtractRouteOptionalParam<ParamWithOptionalRegExp, U> & ExtractRouteParams<Rest, U>
  : T extends `${infer _Start}:${infer ParamWithOptionalRegExp}`
  ? ParamWithOptionalRegExp extends `${infer Param}(${infer _RegExp})${infer Modifier extends
      | '?'
      | '+'
      | '*'
      | ''}`
    ? ExtractRouteOptionalParam<`${Param}${Modifier}`, U>
    : ExtractRouteOptionalParam<ParamWithOptionalRegExp, U>
  : {};
